import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import { comics } from '@/mocks/comics';
import { categories, subcategories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);
  const relatedSub = subcategories.filter((s) => s.parent === slug);
  const categoryComics = comics.filter(
    (c) => c.category === category?.name || relatedSub.some((s) => s.name === c.subcategory)
  );

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: category ? `${category.name}韩漫 - 韩漫之家` : '韩漫分类 - 韩漫之家',
    description: category?.description || '韩漫之家漫画分类',
    url: `http://uyeqdpvovrg.cn/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫之家',
      url: 'http://uyeqdpvovrg.cn/',
    },
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: categoryComics.length,
      itemListElement: categoryComics.map((comic, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'CreativeWork',
          name: comic.title,
          url: `http://uyeqdpvovrg.cn/comic/${comic.id}`,
        },
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={category ? `${category.name}韩漫 - 免费${category.name}韩国漫画在线阅读 - 韩漫之家` : '韩漫分类 - 韩漫之家'}
        description={category ? `韩漫之家${category.name}漫画频道，${category.description}。免费阅读${category.count}+部精选${category.name}题材韩国漫画，支持下拉式在线阅读。` : '韩漫之家漫画分类频道，浏览各类韩国漫画。'}
        keywords={category ? `${category.name}韩漫,免费${category.name}漫画,韩国${category.name}漫画,韩漫之家` : '韩漫分类,韩国漫画'}
        canonicalUrl={`http://uyeqdpvovrg.cn/category/${slug}`}
        schema={categorySchema}
      />
      <Navbar />
      <main className="pt-16 min-h-screen bg-background-50">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category ? `${category.name}漫画` : '分类浏览' },
          ]}
        />

        {/* Category switcher tabs */}
        <div className="w-full px-4 md:px-6 py-4 border-b border-background-200/70">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <span className="text-sm font-medium text-foreground-700 whitespace-nowrap mr-2">全部分类：</span>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                }`}
              >
                {cat.name} <span className="text-xs opacity-70">({cat.count})</span>
              </Link>
            ))}
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-8">
          {category && (
            <div className="relative rounded-lg overflow-hidden h-[200px] md:h-[260px] mb-10">
              <img
                src={category.image}
                alt={`${category.name}韩漫`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/70 to-foreground-950/30"></div>
              <div className="absolute bottom-0 left-0 p-6 md:p-8">
                <h1 className="text-2xl md:text-3xl font-heading text-background-50">{category.name}漫画</h1>
                <p className="mt-2 text-sm text-background-100/80 max-w-lg">{category.description}</p>
                <p className="mt-2 text-xs text-background-100/60">共 {categoryComics.length} 部作品</p>
              </div>
            </div>
          )}

          {relatedSub.length > 0 && (
            <div className="flex items-center gap-3 mb-8">
              <span className="text-sm font-medium text-foreground-700">子分类：</span>
              {relatedSub.map((sub) => (
                <span
                  key={sub.slug}
                  className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full"
                >
                  {sub.name}
                </span>
              ))}
            </div>
          )}

          <h2 className="text-lg font-semibold text-foreground-900 mb-6">全部作品</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5">
            {categoryComics.map((comic) => (
              <ComicCard
                key={comic.id}
                id={comic.id}
                title={comic.title}
                author={comic.author}
                cover={comic.cover}
                category={comic.category}
                status={comic.status}
                rating={comic.rating}
                views={comic.views}
                latestChapter={`第${comic.chapters[0].num}话`}
                isNew={comic.chapters.some((ch) => ch.isNew)}
              />
            ))}
          </div>

          {categoryComics.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-4xl text-foreground-400"></i>
              <p className="mt-4 text-sm text-foreground-500">该分类暂无漫画，请浏览其他分类</p>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}